import { useState } from "react";
import type { Period } from "@yarnalytics/shared";
import { useStore } from "@/lib/store";
import { useHeatmap } from "@/lib/hooks";
import { PeriodSelector } from "@/components/PeriodSelector";
import { HeatmapGrid } from "@/components/HeatmapGrid";

const modeOptions: { value: "total" | "likes" | "replies"; label: string }[] = [
  { value: "total", label: "Combined" },
  { value: "likes", label: "Likes" },
  { value: "replies", label: "Replies" },
];

export function HeatmapPage() {
  const [period, setPeriod] = useState<Period>("7d");
  const [mode, setMode] = useState<"total" | "likes" | "replies">("total");
  const activeAccountId = useStore((s) => s.activeAccountId);
  const isPro = useStore((s) => s.isPro());

  const { data, isLoading, isError } = useHeatmap(activeAccountId, period);

  if (!isPro) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <h2 className="text-xl font-bold">Engagement Heatmap</h2>
        <p className="mt-2 text-sm text-neutral-500">
          Upgrade to Pro to see your peak engagement hours.
        </p>
        <a
          href="/dashboard/billing"
          className="mt-4 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 transition-colors"
        >
          Upgrade to Pro
        </a>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Engagement Heatmap</h1>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* Mode toggle */}
      <div className="flex gap-1 mb-6">
        {modeOptions.map((m) => (
          <button
            key={m.value}
            onClick={() => setMode(m.value)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              mode === m.value
                ? "bg-black text-white"
                : "text-neutral-400 hover:text-black"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Heatmap */}
      <div className="rounded-xl border border-border bg-white p-5">
        <p className="mb-4 text-xs text-neutral-400">
          Shows when your audience is most active. Darker = more engagement.
          Times are in UTC.
        </p>

        {isLoading ? (
          <div className="h-56 animate-pulse rounded-lg bg-surface" />
        ) : isError ? (
          <div className="rounded-lg border border-danger/20 bg-red-50 p-4 text-sm text-danger">
            We couldn’t load the heatmap. Please try again shortly.
          </div>
        ) : data ? (
          <HeatmapGrid cells={data.cells} mode={mode} />
        ) : (
          <div className="h-56 flex items-center justify-center text-sm text-neutral-400">
            No heatmap data yet.
          </div>
        )}
      </div>
    </div>
  );
}
